import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import StoreBadge from './StoreBadge';
import apiService from '../services/nextlevelgames-api';

const MAX_EVENTS = 20;

const STORES = [
  { key: 'muncie', label: 'Muncie', locationName: 'Next Level Games Muncie' },
  { key: 'carmel', label: 'Carmel', locationName: 'Next Level Games Carmel' },
];

const formatDate = (iso, tz) =>
  new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', timeZone: tz || 'America/New_York',
  });

const formatTime = (iso, tz) =>
  new Date(iso).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', timeZone: tz || 'America/New_York',
  });

const EventsShowcase = () => {
  const [activeKey, setActiveKey] = useState(STORES[0].key);
  const [eventsByStore, setEventsByStore] = useState({});
  const [errorKey, setErrorKey] = useState(null);
  const scrollerRef = useRef(null);

  const store = STORES.find((s) => s.key === activeKey);

  // Lazy-load each store's events the first time its tab is opened, then cache them.
  useEffect(() => {
    if (eventsByStore[activeKey]) return undefined;
    let cancelled = false;
    setErrorKey(null);
    apiService
      .getEvents([store.locationName], false)
      .then((data) => {
        if (!cancelled) {
          setEventsByStore((prev) => ({ ...prev, [activeKey]: (data.items || []).slice(0, MAX_EVENTS) }));
        }
      })
      .catch(() => {
        if (!cancelled) setErrorKey(activeKey);
      });
    return () => {
      cancelled = true;
    };
  }, [activeKey, store, eventsByStore]);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTo({ left: 0 });
  }, [activeKey]);

  const scrollByPage = useCallback((dir) => {
    const el = scrollerRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: 'smooth' });
  }, []);

  const events = eventsByStore[activeKey];
  const eventsLink = `/events?location=${activeKey}`;

  return (
    <section className="py-8">
      <div className="container">
        <div className="text-center mb-4">
          <h2 className="heading-block text-4xl mb-2">
            Upcoming <span className="text-gradient">Events</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Tournaments, casual play nights, and special events. Compete for prizes, meet fellow players, and level up your game!
          </p>
        </div>

        {/* Store picker */}
        <div className="relative flex flex-col gap-2 mb-4 sm:block">
        <div className="flex gap-2 justify-center" role="tablist">
          {STORES.map((s) => (
            <button
              key={s.key}
              role="tab"
              aria-selected={s.key === activeKey}
              onClick={() => setActiveKey(s.key)}
              className={`px-5 py-2 rounded-full border text-sm font-semibold transition-colors ${
                s.key === activeKey
                  ? 'bg-primary-600 border-primary-600 text-white shadow'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-primary-300 hover:text-primary-600'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
          <Link to={eventsLink} className="btn-viewall self-end sm:absolute sm:right-0 sm:top-1">
            View all
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {/* Event row */}
        <div className="relative">
          <button
            onClick={() => scrollByPage(-1)}
            className="hidden md:flex absolute -left-4 top-1/3 z-10 p-2 rounded-full bg-white shadow-lg hover:bg-gray-50"
            aria-label="Scroll left"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => scrollByPage(1)}
            className="hidden md:flex absolute -right-4 top-1/3 z-10 p-2 rounded-full bg-white shadow-lg hover:bg-gray-50"
            aria-label="Scroll right"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <div
            ref={scrollerRef}
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 px-1 min-h-[300px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {!events && errorKey !== activeKey && (
              <div className="flex-1 flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
              </div>
            )}
            {errorKey === activeKey && !events && (
              <p className="flex-1 text-center text-gray-500 self-center">
                Couldn't load events right now.{' '}
                <Link to={eventsLink} className="text-primary-600 font-medium">View the events page instead</Link>
              </p>
            )}
            {events && events.length === 0 && (
              <p className="flex-1 text-center text-gray-500 self-center">
                No upcoming events at {store.label} right now — check back soon.
              </p>
            )}
            {events &&
              events.map((event) => (
                <Link
                  key={event.id}
                  to={eventsLink}
                  className="snap-start flex-shrink-0 w-60 sm:w-64 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow overflow-hidden flex flex-col"
                >
                  <div className="h-36 bg-gray-100 overflow-hidden">
                    {event.imageUrl ? (
                      <img src={event.imageUrl} alt={event.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-4xl text-gray-300">🎮</div>
                    )}
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    {event.event_start_at && (
                      <div className="flex items-center text-primary-700 text-xs font-semibold mb-1">
                        <Calendar className="w-3 h-3 mr-1 flex-shrink-0" />
                        {formatDate(event.event_start_at, event.event_timezone)} · {formatTime(event.event_start_at, event.event_timezone)}
                      </div>
                    )}
                    <h3 className="font-semibold text-gray-900 leading-tight line-clamp-2 mb-2">{event.name}</h3>
                    <div className="mt-auto flex items-center justify-between gap-2">
                      <StoreBadge name={store.label} />
                      <span className="text-sm font-bold text-primary-600">
                        {event.price > 0 ? `$${event.price.toFixed(2)}` : 'Free'}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default EventsShowcase;
